/**
 * URL-shape validation for QR-code scanning and manual source-URL entry.
 *
 * Shared by the web client (`routes/local-sources.tsx`) and the mobile app
 * (`utils/url-validation.ts`). Hoisted here so the two cannot diverge again:
 * both surfaces accept the same kinds of source endpoints and, crucially,
 * reject the same single-label garbage hosts (MP-167).
 *
 * The MP-167 bug: a bare single-label host like `abc` parses cleanly —
 * `new URL('http://abc')` is syntactically valid — so a naive validator let it
 * through, the viewer opened, and the user landed on a permanent blank
 * "waiting" screen instead of an "invalid address" error. The fix is a
 * hostname-shape guard ({@link isHostnameUrlShaped}) that only accepts hosts
 * that could plausibly resolve.
 *
 * Environment-agnostic (no React, no React Native, no DOM) — safe to import
 * from any package.
 */
const URL_PATTERN = /^https?:\/\/.+/i;
const IPV4_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/;
/**
 * A hostname is "URL-shaped" if it's either:
 *   - an IPv4 address (e.g. `192.168.1.50`)
 *   - an IPv6 literal (already wrapped in brackets by the URL parser)
 *   - a multi-label DNS name (contains at least one dot, like `polygraf.app`
 *     or `host.local`)
 *   - the well-known single-label `localhost`
 *
 * Single-label hostnames like `abc` or `test` pass `new URL(...)` parsing but
 * will never resolve — accepting them routes users to a hopeless blank screen
 * (MP-167). Reject them so a form-level "invalid address" alert fires instead.
 */
export function isHostnameUrlShaped(hostname) {
    if (!hostname)
        return false;
    if (hostname.startsWith('[') && hostname.endsWith(']'))
        return true; // IPv6 literal
    if (IPV4_PATTERN.test(hostname))
        return true;
    if (hostname === 'localhost')
        return true;
    return hostname.includes('.');
}
/**
 * Normalize a raw string into a URL by adding `http://` when no scheme is
 * present. Empty / whitespace-only input is returned unchanged (empty) so
 * downstream validators reject it.
 */
export function normalizeUrl(input) {
    const trimmed = input.trim();
    if (!trimmed)
        return trimmed;
    if (/^https?:\/\//i.test(trimmed))
        return trimmed;
    return `http://${trimmed}`;
}
/**
 * True when the input is a valid http/https URL pointing to a host that could
 * plausibly resolve. Single-label hostnames (`abc`, `test`) are rejected even
 * though they parse cleanly — see MP-167 for the user-visible symptom (blank
 * "waiting" viewer instead of a validation error).
 */
export function isValidPolygrafUrl(input) {
    const normalized = normalizeUrl(input);
    if (!URL_PATTERN.test(normalized))
        return false;
    try {
        const url = new URL(normalized);
        if (url.protocol !== 'http:' && url.protocol !== 'https:')
            return false;
        return isHostnameUrlShaped(url.hostname);
    }
    catch {
        return false;
    }
}
/**
 * Parse a raw scanned/typed value into a `URL`, or `null` when it isn't a
 * usable http(s) source endpoint. A bare `host:port` (no scheme) is assumed to
 * be a plaintext LAN source and gets `http://` prepended. Single-label garbage
 * hosts return `null` (MP-167) so callers surface an "invalid address" error
 * rather than opening a blank viewer.
 */
export function parseSourceUrl(raw) {
    const normalized = normalizeUrl(raw);
    if (!URL_PATTERN.test(normalized))
        return null;
    try {
        const url = new URL(normalized);
        if (url.protocol !== 'http:' && url.protocol !== 'https:')
            return null;
        if (!isHostnameUrlShaped(url.hostname))
            return null;
        return url;
    }
    catch {
        return null;
    }
}
/**
 * True when `url` shares the exact same origin (scheme + host + port) as
 * `apiUrl`. The single gate for attaching the session bearer token (M1): it
 * must ride ONLY requests bound for our own API — never a LAN / mDNS-direct
 * source, and never an absolute cross-origin HLS segment that a manifest may
 * reference (hls.js would otherwise fetch such a segment directly and leak the
 * token to an untrusted host). Both arguments must be absolute URLs; empty or
 * unparseable input returns false (fail closed).
 */
export function isSameApiOrigin(url, apiUrl) {
    if (!url || !apiUrl)
        return false;
    try {
        return new URL(url).origin === new URL(apiUrl).origin;
    }
    catch {
        return false;
    }
}
/**
 * Return `url` with any embedded userinfo (`user:pass@`) removed. RTSP camera
 * endpoints are commonly `rtsp://user:pass@host/stream`; a read-only API that
 * echoes a service endpoint to non-manager viewers must not leak the camera
 * password. The host/scheme/path are preserved so the value stays usable for
 * presence checks and LAN-vs-cloud detection. Unparseable input returns
 * `null` (never echo a string we couldn't sanitize).
 */
export function stripUrlCredentials(url) {
    try {
        const u = new URL(url);
        u.username = '';
        u.password = '';
        return u.toString();
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=url-validation.js.map